import { CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PrepaidCurrentPlan, PrepaidCurrentPlanItem, PricingGroup } from '../../../shared/models/pricing.model';
import { AuthUser } from '../../../shared/models/user.model';
import { InvoiceService } from '../../../shared/services/invoice.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PricingService } from '../../../shared/services/pricing.service';
import { UsersService } from '../../../shared/services/users.service';
import { Part } from '../../billing-progress/billing-progress.component';

@Component({
  standalone: false,
  selector: 'app-packages-index',
  templateUrl: './packages-index.component.html',
  styleUrls: ['./packages-index.component.scss'],
})
export class PackagesIndexComponent implements OnInit {
  user: AuthUser;
  models: PricingGroup[] = [];
  idsActive: number[] = [];
  loading = false;

  currentBalance = 0;
  currentPlan: PrepaidCurrentPlan;

  progress: { total: number; current: number; parts: Part[] };

  constructor(
    public pricingService: PricingService,
    public invoiceService: InvoiceService,
    public userService: UsersService,
    public router: Router,
    public notificationService: NotificationService,
    private currency: CurrencyPipe,
    titleService: Title
  ) {
    titleService.setTitle('Select package');
  }

  ngOnInit() {
    this.loading = true;

    this.userService.getAuthUser().then(user => {
      this.user = user;
      this.loadPricingGroups();
    });

    this.userService.balanceUpdates.subscribe(_ => this.update(_));
  }

  private loadPricingGroups(): void {
    forkJoin([this.pricingService.myPrepaidPricingGroups(), this.pricingService.myPrepaidActivePricingGroup()]).subscribe({
      next: ([groups, activeGroup]) => {
        this.handlePricingGroups(groups, activeGroup);
      },
      error: error => {
        this.handlePricingError(error);
      },
    });
  }

  private handlePricingGroups(groups: any[], activeGroup: any): void {
    this.addActiveGroupId(activeGroup);

    const formattedGroups = groups.map(group => {
      group.bundles = group.bundles.map(bundle => this.formatBundle(bundle));
      return group;
    });

    this.models = formattedGroups.sort((a, b) => a.totalCommitment - b.totalCommitment);
    this.loading = false;
  }

  private formatBundle(bundle: any): any {
    bundle.creditValidity = PricingService.formatValidForSeconds(bundle.validForSeconds);
    bundle.offerVisibleUntil = null;

    if (typeof bundle.visibleUntil === 'string' && bundle.visibleUntil.indexOf('9999') === -1) {
      bundle.offerVisibleUntil = new Date(bundle.visibleUntil);
    }

    return bundle;
  }

  private handlePricingError(error: any): void {
    this.loading = false;

    this.notificationService.error({
      title: 'Pricing',
      message: 'An error occurred while creating the pricing groups',
      serviceName: 'API GATEWAY',
      requestMessage: error.statusText,
      requestCode: error.status,
      ts: error.timestamp ? error.timestamp : null,
    });
  }

  private addActiveGroupId(id: number) {
    if (!this.idsActive.includes(id)) {
      this.idsActive.push(id);
    }
  }

  private update(balance: number) {
    this.currentBalance = balance;
    this.pricingService.getPrepaidPlan().subscribe(plan => {
      plan.items.forEach(_ => this.addActiveGroupId(_.pricingGroupId));
      this.currentPlan = plan;
      this.progress = this.createProgress(plan.items);
    });
  }

  private createProgress(items: PrepaidCurrentPlanItem[]) {
    return {
      total: items.length * 100,
      current: items.map(_ => _.progressPercent).reduce((a, b) => a + b, 0),
      parts: items.map(i => {
        const mtRate = this.money(i.ratePerTest);
        const cliRate = this.money(i.ratePerUnitCli);
        const moRate = this.money(i.ratePerUnitMo);

        let tooltip = `Price per MT test: <strong>${mtRate}</strong><br>`;
        if (i.ratePerUnitCli > 0) {
          tooltip += `Price per CLI test: <strong>${cliRate}</strong><br>`;
        }
        if (i.ratePerUnitMo > 0) {
          tooltip += `Price per MO test: <strong>${moRate}</strong><br>`;
        }

        if (i.ratePerUnitFlashCall > 0) {
          tooltip += `Price per Flash call test: <strong>${this.money(i.ratePerUnitFlashCall)}</strong>`;
        }

        return {
          max: 0,
          label: `<span class="text-primary">${this.money(i.used)}</span>/${this.money(i.total)} balance used`,
          tooltip,
        };
      }),
    };
  }

  onSelectPricingGroup(pricingGroupId: number) {
    this.loading = true;
    this.invoiceService.create({ pricingGroupId: pricingGroupId, userId: this.user.id }).subscribe({
      next: invoice => {
        this.loading = false;
        this.notificationService.success('Invoice created', 'Invoices');
        this.router.navigate(['invoices'], { queryParams: { payment: invoice.id } }).then();
      },
      error: error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Invoices',
          message: 'An error occurred while creating the invoice',
          serviceName: 'API GATEWAY',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
      },
    });
  }

  getFullNameServiceType(serviceTypeCode: string): string {
    const map = {
      MT: 'Mobile terminated',
      API: 'Api',
      MO: 'Mobile Originated',
      FLASH_CALL: 'Flash call',
      STATISTICS: '',
    };
    if (map[serviceTypeCode]) {
      return map[serviceTypeCode];
    }
    return serviceTypeCode;
  }

  private money(val: number): string {
    return this.currency.transform(val / 1000, 'EUR', 'symbol', '1.1-3');
  }
}
